import classNames from "classnames";
import { FC, ReactNode, RefObject, useEffect, useRef } from "react";

const NavItem: FC<{
  parentRef: RefObject<null>;
  children: ReactNode;
  onClick: () => void;
  className: string;
  selected: boolean;
}> = ({ parentRef, children, onClick = () => {}, className, selected }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (selected) {
      if (ref.current && parentRef.current) {
        const ref_ = ref.current as HTMLButtonElement;
        const parentRef_ = parentRef.current as HTMLDivElement;

        const relativeLeft =
          ref_.getBoundingClientRect().left -
          parentRef_.getBoundingClientRect().left +
          parentRef_.scrollLeft;

        parentRef_.scrollLeft = relativeLeft - 32;
      }
    }
    // eslint-disable-next-line
  }, [selected]);

  return (
    <button
      type="button"
      ref={ref}
      onClick={() => {
        onClick();
      }}
      className={className}
    >
      {children}
    </button>
  );
};

export const TabNav: FC<{
  navs: {
    title: string | ReactNode;
    to: string;
    onClick: (val: string) => void;
    count?: number;
  }[];
  className?: string;
  tabClassName?: string;
  activeTab: string;
  variant: "nav" | "switcher";
  theme?: "white" | "green";
}> = ({
  navs = [],
  className = "",
  tabClassName = "",
  activeTab,
  variant = "nav",
  theme = "white",
}) => {
  const navRef = useRef(null);

  return (
    <div
      className={classNames(
        `hide-scroll-bar flex flex-row gap-3 overflow-x-scroll md:gap-4`,
        `!scroll-smooth transition-all duration-200 max-w-full`,
        {
          "p-1 rounded-md md:rounded-lg bg-light-grey-bg border border-[#E9EAEB] !gap-2":
            variant === "switcher" && theme === "white",
          "p-1 rounded-md bg-white border !gap-2":
            variant === "switcher" && theme === "green",
        },
        className
      )}
      ref={navRef}
    >
      {navs
        .filter((item) => item !== undefined)
        .map(({ title, to, onClick, count }, i) => {
          const key = `TabNav-${i}-${title}`;

          return (
            <NavItem
              parentRef={navRef}
              key={key}
              selected={activeTab === to}
              className={classNames(
                `relative flex-1 justify-center flex min-w-max cursor-pointer items-center gap-1 text-center text-base transition-all duration-300`,
                {
                  "text-pry-dark-green-400/40 ":
                    variant === "nav" && activeTab !== to,

                  "text-pry-dark-green-400/60 ":
                    variant === "switcher" && activeTab !== to,

                  "!text-pry-green-200 font-medium":
                    variant === "nav" && activeTab === to,

                  "p-1 font-medium": variant === "nav",

                  "px-[32px] text-sm md:text-base py-1.5 md:py-1 rounded-[6px] text-neut-500 justify-center font-medium":
                    variant === "switcher",

                  "bg-white shadow-sm !text-neut-700 font-medium border border-[#D5D7DA]":
                    variant === "switcher" &&
                    activeTab === to &&
                    theme === "white",

                  "bg-pry-dark-green-100 shadow-sm !text-white font-medium":
                    variant === "switcher" &&
                    activeTab === to &&
                    theme === "green",
                },
                `${tabClassName}`
              )}
              onClick={() => {
                onClick(to);
              }}
            >
              {title}

              {!!count ? (
                <p
                  className={classNames(
                    `
                    transition-all duration-500
                    text-xs px-1 min-h-[20px] min-w-[20px] rounded-full
                    flex items-center justify-center
                    text-pry-dark-green-300/30
                    bg-pry-dark-green-300/10`,
                    {
                      "!bg-pry-green-200 !text-white !font-medium":
                        variant === "nav" && activeTab === to,
                      "!text-white !bg-pry-dark-green-400":
                        variant === "switcher" && activeTab === to,
                    }
                  )}
                >
                  {count}
                </p>
              ) : undefined}

              {variant === "nav" && (
                <div
                  className={`absolute bottom-0 left-0 right-full w-0 border-b-2 border-transparent transition-all duration-500 ${
                    activeTab === to &&
                    "!border-pry-green-200 !right-full !w-full rounded-full"
                  }`}
                />
              )}
            </NavItem>
          );
        })}
    </div>
  );
};
